import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ToastProvider } from '@/components/ui/toast';
import AppLayout from '@/layouts/AppLayout';
import LoginPage from '@/pages/LoginPage';
import LandingPage from '@/pages/LandingPage';
import DashboardPage from '@/pages/DashboardPage';
import TimelinePage from '@/pages/TimelinePage';
import EntryDetailPage from '@/pages/EntryDetailPage';
import RecordPage from '@/pages/RecordPage';
import SettingsPage from '@/pages/SettingsPage';
import ReviewPage from '@/pages/ReviewPage';
import PremiumPage from '@/pages/PremiumPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsPage from '@/pages/TermsPage';
import CommercialTransactionsPage from '@/pages/CommercialTransactionsPage';
import SupportPage from '@/pages/SupportPage';
import ChangelogPage from '@/pages/ChangelogPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { Loader2 } from 'lucide-react';
import { useEffect, lazy, Suspense } from 'react';
import { initFont } from '@/lib/fontManager';
import { scheduleNotification } from '@/lib/notifications';

/** デプロイ後に古いチャンクが消えた場合、自動リロードで最新版を取得 */
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise(() => {}); // reload 中は resolve しない
    })
  );
}

const DictionaryPage = lazyWithRetry(() => import('@/pages/DictionaryPage'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminDashboardPage'));
const AdminTicketsPage = lazyWithRetry(() => import('@/pages/admin/AdminTicketsPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { token, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (token) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout>
      <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}>
        {children}
      </Suspense>
    </AppLayout>
  );
}

function HomeRoute() {
  const { token, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (token) {
    return <AppLayout><DashboardPage /></AppLayout>;
  }

  return <LandingPage />;
}

function applyThemeMode(mode: string) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const shouldBeDark = mode === 'dark' || (mode === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', shouldBeDark);
}

function ThemeInit() {
  const { token, isLoading } = useAuth();

  useEffect(() => {
    // ログイン前はライトテーマ固定
    if (!isLoading && !token) {
      applyThemeMode('light');
      return;
    }
    if (isLoading) return;

    const saved = localStorage.getItem('theme') || 'light';
    applyThemeMode(saved);

    // システムモード: OS設定の変更をリッスン
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if ((localStorage.getItem('theme') || 'light') === 'system') {
        applyThemeMode('system');
        import('@/lib/themeManager').then(({ reapplyThemeForMode }) => reapplyThemeForMode());
      }
    };
    mq.addEventListener('change', handler);

    // フォントサイズ復元（新形式: fontSizeIndex、旧形式: fontSize からの移行対応）
    const FONT_PX = ['15px', '16px', '18px', '20px', '22px'];
    const fsi = localStorage.getItem('fontSizeIndex');
    if (fsi !== null) {
      const px = FONT_PX[Number(fsi)];
      if (px) document.documentElement.style.fontSize = px;
    } else {
      const legacy = localStorage.getItem('fontSize');
      if (legacy === 'small') document.documentElement.style.fontSize = '15px';
      else if (legacy === 'large') document.documentElement.style.fontSize = '20px';
    }
    // Apply color theme
    import('@/lib/themeManager').then(({ loadSavedTheme }) => loadSavedTheme());
    // Apply saved font
    initFont();
    // Schedule notification reminder (local fallback)
    scheduleNotification();
    // Re-subscribe push notification (keeps subscription fresh)
    import('@/lib/pushNotifications').then(({ subscribePush }) => {
      const settings = JSON.parse(localStorage.getItem('notificationSettings') || '{}');
      if (settings.enabled) subscribePush();
    });

    return () => mq.removeEventListener('change', handler);
  }, [token, isLoading]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <ThemeInit />
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
            <Route path="/entry/:id" element={<ProtectedRoute><EntryDetailPage /></ProtectedRoute>} />
            <Route path="/record" element={<ProtectedRoute><RecordPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/review" element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
            <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
            <Route path="/dictionary" element={<ProtectedRoute><Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><DictionaryPage /></Suspense></ProtectedRoute>} />
            <Route path="/changelog" element={<ProtectedRoute><ChangelogPage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
            <Route path="/admin/tickets" element={<AdminRoute><AdminTicketsPage /></AdminRoute>} />
            <Route path="/auth/reset" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/commercial-transactions" element={<CommercialTransactionsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
